/**
 * URL Sanitizer Utility
 * 
 * Validates and sanitizes URLs to prevent XSS attacks by blocking dangerous protocols
 * and ensuring only safe HTTP/HTTPS URLs are allowed.
 */

export interface UrlValidationResult {
  isValid: boolean;
  sanitizedUrl: string;
}

/**
 * Sanitizes and validates a URL to prevent XSS attacks
 * 
 * @param url - The URL string to validate (can be undefined)
 * @returns An object containing validation result and sanitized URL
 * 
 * @example
 * ```typescript
 * const { isValid, sanitizedUrl } = sanitizeUrl('https://example.com/image.jpg');
 * if (isValid) {
 *   // Use sanitizedUrl safely
 * }
 * ```
 */
export const sanitizeUrl = (url: string | undefined): UrlValidationResult => {
  // Return invalid for undefined or empty URLs
  if (!url || typeof url !== 'string') {
    return { isValid: false, sanitizedUrl: '' };
  }

  // Normalize URL by trimming whitespace
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return { isValid: false, sanitizedUrl: '' };
  }

  // Check for relative URLs starting with / (but not // which is protocol-relative)
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    // Valid relative URL
    return { isValid: true, sanitizedUrl: trimmedUrl };
  }

  // Parse absolute URL using URL constructor
  try {
    const parsedUrl = new URL(trimmedUrl);
    const protocol = parsedUrl.protocol.toLowerCase();

    // Only allow http: and https: protocols
    if (protocol === 'http:' || protocol === 'https:') {
      return { isValid: true, sanitizedUrl: trimmedUrl };
    }


    // Block dangerous protocols: javascript:, data:, vbscript:, file:, etc.
    return { isValid: false, sanitizedUrl: '' };
  } catch (error) {
    // URL parsing failed (malformed URL)
    return { isValid: false, sanitizedUrl: '' };
  }
};

